import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FilmSlate, Plus, Copy, Archive, Trash, ArrowClockwise, Clock, Stack, CheckCircle } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { EmptyState } from "@/promotions/components/EmptyState";
import { CardsSkeleton } from "@/promotions/components/Skeleton";
import { videoApi } from "../useCreative";
import { fmtDuration } from "../videoUtils";
import { fmtRelative } from "../creativeUtils";

export default function VideoStudio() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setProjects(await videoApi.listProjects(archived)); }
    catch { setProjects([]); }
    setLoading(false);
  }, [archived]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const title = window.prompt("Project title", "Untitled project");
    if (!title) return;
    setCreating(true);
    try {
      const p = await videoApi.createProject({ title, aspect_ratio: "16:9" });
      toast.success("Project created");
      nav(`/creative/video/${p.id}`);
    } catch { toast.error("Could not create project"); }
    setCreating(false);
  };

  const dup = async (id) => { await videoApi.duplicateProject(id); toast.success("Duplicated"); load(); };
  const arch = async (id, val) => { await videoApi.archiveProject(id, val); toast.success(val ? "Archived" : "Restored"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this project permanently?")) return; await videoApi.deleteProject(id); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-8" data-testid="video-studio">
      <PromoPageHeader
        overline="Creative Studio" title="AI Video Studio"
        description="Plan cinematic AI video projects as Storyboards of Scenes — each scene composed with the Visual Prompt Builder and rendered by the best available provider."
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => setArchived((a) => !a)} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent" data-testid="video-toggle-archived">
              <Archive size={14} /> {archived ? "Active" : "Archived"}
            </button>
            <button onClick={load} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent" data-testid="video-refresh">
              <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={create} disabled={creating} className="flex items-center gap-1.5 rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground hover:opacity-90 disabled:opacity-60" data-testid="video-new-project">
              <Plus size={15} weight="bold" /> New Project
            </button>
          </div>
        }
      />

      {loading ? (
        <CardsSkeleton count={3} />
      ) : projects.length === 0 ? (
        <EmptyState Icon={FilmSlate} title={archived ? "No archived projects" : "No video projects yet"}
          description={archived ? "Archived projects will appear here." : "Create your first AI video project to start building a storyboard."} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="video-projects-grid">
          {projects.map((p) => {
            const o = p.overview || {};
            return (
              <div key={p.id} className="group rounded-lg border border-border bg-card p-5 transition-colors hover:border-ai/40" data-testid={`video-project-${p.id}`}>
                <button onClick={() => nav(`/creative/video/${p.id}`)} className="w-full text-left" data-testid={`video-open-${p.id}`}>
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-ai/15 text-ai"><FilmSlate size={18} weight="fill" /></div>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">v{p.version}</span>
                  </div>
                  <h3 className="truncate text-base font-semibold">{p.title}</h3>
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{p.description || "No description"}</p>
                  <div className="mt-4 grid grid-cols-2 gap-y-2 text-xs">
                    <Stat Icon={Stack} label="Scenes" value={o.total_scenes ?? 0} />
                    <Stat Icon={CheckCircle} label="Generated" value={`${o.generated_scenes ?? 0}/${o.total_scenes ?? 0}`} tone="text-success" />
                    <Stat Icon={Clock} label="Duration" value={fmtDuration(o.total_duration_seconds)} />
                    <Stat Icon={ArrowClockwise} label="Modified" value={fmtRelative(o.last_modified)} />
                  </div>
                </button>
                <div className="mt-4 flex items-center gap-1 border-t border-border pt-3 opacity-0 transition-opacity group-hover:opacity-100">
                  <IconBtn Icon={Copy} title="Duplicate" onClick={() => dup(p.id)} testid={`video-dup-${p.id}`} />
                  <IconBtn Icon={Archive} title={archived ? "Restore" : "Archive"} onClick={() => arch(p.id, !archived)} testid={`video-archive-${p.id}`} />
                  <IconBtn Icon={Trash} title="Delete" onClick={() => del(p.id)} danger testid={`video-del-${p.id}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ Icon, label, value, tone }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={13} className="text-muted-foreground" />
      <span className="text-muted-foreground">{label}:</span>
      <span className={`font-medium ${tone || "text-foreground"}`}>{value}</span>
    </div>
  );
}

function IconBtn({ Icon, title, onClick, danger, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent ${danger ? "text-muted-foreground hover:text-destructive" : "text-muted-foreground hover:text-foreground"}`}>
      <Icon size={15} />
    </button>
  );
}
